import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ClipboardList, Users, Home, Wallet, Calendar,
  LogOut, Sparkles, Menu, BadgeDollarSign, MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const navItems = [
  { to: "/", label: "Kontrol Paneli", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/isler", label: "İşler", icon: ClipboardList, testId: "nav-jobs" },
  { to: "/takvim", label: "Takvim", icon: Calendar, testId: "nav-calendar" },
  { to: "/calisanlar", label: "Çalışanlar", icon: Users, testId: "nav-employees" },
  { to: "/musteriler", label: "Müşteriler / Evler", icon: Home, testId: "nav-customers" },
  { to: "/finans", label: "Gelir / Gider", icon: Wallet, testId: "nav-finances" },
  { to: "/hakedis", label: "Hakediş Raporu", icon: BadgeDollarSign, testId: "nav-payroll" },
  { to: "/whatsapp", label: "WhatsApp Bildirimleri", icon: MessageCircle, testId: "nav-whatsapp" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const currentTitle = navItems.find((n) => n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))?.label || "HijyenPro";

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-white border-r border-slate-200 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 border-b border-slate-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="font-bold text-lg text-slate-900" style={{ fontFamily: "Outfit" }}>HijyenPro</div>
            <div className="text-xs text-slate-500">Temizlik Yönetimi</div>
          </div>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-50 text-emerald-800 shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <it.icon className="h-4.5 w-4.5 shrink-0" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="text-xs text-slate-500">Giriş yapıldı</div>
          <div className="font-semibold text-sm text-slate-900 truncate" data-testid="user-name">{user?.name || user?.email}</div>
          <Button
            variant="ghost" size="sm"
            className="mt-2 w-full justify-start text-slate-600 hover:text-red-600"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-lg border-b border-slate-200 flex items-center px-4 lg:px-8">
          <button className="lg:hidden mr-3" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }} data-testid="page-title">{currentTitle}</h1>
          <div className="ml-auto text-xs text-slate-500 hidden sm:block">
            {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </header>
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
